import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const fmt = (s?: string) => (s ? new Date(s).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");

function Timeline({ events }: { events: any[] }) {
  return (
    <ol className="relative ml-2 border-l-2 border-[#E2E8F0]" data-testid="code-history-timeline">
      {events.map((e, i) => (
        <li key={i} className="mb-5 ml-5" data-testid={`code-history-event-${i}`}>
          <span className={`absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full ${e.event.startsWith("Blocked") ? "bg-[#B91C1C]" : "bg-[#1B365D]"}`} />
          <p className="text-[15px] font-semibold text-[#0F172A]">{e.event}</p>
          <p className="text-xs text-[#64748B]">{fmt(e.at)}{e.by ? ` · ${e.by}` : ""}</p>
          {e.note && <p className="mt-1 text-xs italic text-[#475569]">“{e.note}”</p>}
        </li>
      ))}
    </ol>
  );
}

export function CodeHistoryDialog({ code, onClose }: { code: string | null; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null);
    setErr("");
    if (code) {
      api.get("/admin/codes/history", { params: { code } }).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
    }
  }, [code]);
  const c = data?.code;
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="code-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Code history <span className="font-mono tracking-wider">{code}</span></DialogTitle>
        </DialogHeader>
        {err && <p className="text-sm text-[#B91C1C]" data-testid="code-history-error">{err}</p>}
        {!data && !err && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#64748B]" />}
        {c && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 rounded-xl bg-[#F1EFEA] p-4 text-sm" data-testid="code-history-summary">
              <div><p className="text-xs text-[#64748B]">Status</p><StatusBadge status={c.status} testId="code-history-status" /></div>
              <div><p className="text-xs text-[#64748B]">Organization</p><p className="font-medium">{c.org_name}</p></div>
              <div><p className="text-xs text-[#64748B]">Expires</p><p>{fmt(c.expires_at)}</p></div>
              <div><p className="text-xs text-[#64748B]">Applicant</p>
                {data.application_id ? <Link to={`/admin/cases/${data.application_id}`} className="text-[#1B365D] hover:underline" data-testid="code-history-applicant-link">{data.applicant || "View application"}</Link> : <p data-testid="code-history-applicant">{data.applicant || "—"}</p>}
              </div>
            </div>
            <Timeline events={data.events} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
